import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation} from '@angular/core';
import {ComponentDefinition} from '@formbird/types';
import {ChangedDocumentService, FormbirdInjectorService, NotificationService} from 'formbird-services';
import {AbstractComponent} from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import {ComponentSetupService} from '../../../../formbird-sc-shared/src/lib/component-setup.service';

interface ButtonComponentDefinition extends ComponentDefinition {
  caption?: string;
  onClick?: string;
  style?: string;
  icon?: string;
  class?: string;
  color?: string;
  widthType?: 'fitText' | 'fill' | 'fixed';
  buttonWidth?: string;
  buttonJustification?: string;     // Config field for button alignment (applied via justify-self)
  textJustification?: string;       // Config field for text alignment inside the button (applied via text-align)      
}

@Component({
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ButtonComponent extends AbstractComponent<ButtonComponentDefinition> implements OnInit {
  // injected into the button onClick fn
  $injector: any;

  btnLabel: string;
  defaultStyle = 'btn btn-default';
  primaryStyle = 'btn btn-primary';

  icon = '';
  color;
  widthType: 'fitText' | 'fill' | 'fixed';
  buttonJustification?: string;     // New buttonJustification config field
  textJustification?: string;       // New textJustification config field for text inside the button
  buttonWidth: string;

  constructor(
    protected changedDocumentService: ChangedDocumentService,
    protected formbirdInjectorService: FormbirdInjectorService,
    protected cdr: ChangeDetectorRef,
    protected componentSetupService: ComponentSetupService,
    private notificationService: NotificationService,
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdr);

    this.$injector = this.formbirdInjectorService;
  }

  ngOnInit(): void {


    this.disabled = this.componentDefinition.enabled === false;

    this.btnLabel = this.componentDefinition.caption ? this.componentDefinition.caption : this.componentDefinition.label;

    this.icon = this.componentDefinition.icon ?? '';

    this.primaryStyle = this.getPrimaryStyle();

    if (!(typeof this.componentDefinition.color === 'undefined' || this.componentDefinition.color === null)) {
      this.color = this.componentDefinition.color;
    }

     // Set the default value for widthType if not specified
     this.widthType = this.componentDefinition.widthType ?? 'fitText';
     this.buttonWidth = this.componentDefinition.buttonWidth ?? '';

    // Set the default value for buttonJustification to 'left' if not provided
    this.buttonJustification = this.componentDefinition.buttonJustification ?? 'left';

    // Set the default value for textJustification to 'left' if not provided
    this.textJustification = this.componentDefinition.textJustification ?? 'left';

    const compSub = this.changedDocumentService.watchComponentDef(this.fieldName, this.template.documentId).subscribe(tplItem => {

      this.onComponentDefChanged(tplItem);
    });
    this.subs.add(compSub);

    this.setupDocumentCreatedDetection();
  }

  getPrimaryStyle() {
    try {
    if (this.componentDefinition.hasOwnProperty('style')) {
      return this.componentDefinition.style;
    } else {
      return this.defaultStyle;
    }
    } catch (err) {
      const errMessage = `Syntax error in button handler for button '${this.fieldName}' : \n ${err.message}`;
      console.error(errMessage);
      console.error(err);
      this.notificationService.error(errMessage);
    }
  }

  onFieldChange() {
    if (this.disabled) {
      return;
    }
    try {
      const strFn = this.componentDefinition.onClick;
      if (strFn) {
        let matchedFunction = strFn.match(/\{.*\}/g)[0];
        if (matchedFunction.indexOf('$injector') > -1) {
          matchedFunction = matchedFunction.replace('$injector', 'this.$injector');
        }

        const changeFunction = new Function(matchedFunction).bind(this);
        changeFunction();

      } else {
        // call changedDocumentService with true to trigger an OnFieldChange ruleset
        this.changedDocumentService.executeOnFieldChangeWithNoChanges(this);
      }
    } catch (err) {
      if (err instanceof SyntaxError) {
        const errMessage = `Syntax error in button handler for button '${this.fieldName}' : \n ${err.message}`;
        console.error(errMessage);
        console.error(err);
        this.notificationService.error(errMessage);

      } else {
        const errMessage = `Error in button handler for button '${this.fieldName}' : \n ${err.message}`;
        console.error(errMessage);
        console.error(err);
        this.notificationService.error(errMessage);
      }
    }
  }

  getWidthTypeClass(): string[] {
    const classes = [];
    switch (this.widthType) {
      case 'fill':
        classes.push('btn-fill');
        break;
      case 'fixed':
        classes.push('btn-fixed');
        break;
      default:
        classes.push('btn-fit-text');
    }
  
    return classes;
  }

  getButtonClasses(): string[] {
    const classes = [];
  
    // Add the primary style
    classes.push(this.primaryStyle);
  
    // Add the width type related classes
    classes.push(...this.getWidthTypeClass());
  
    // Add custom classes from the component definition if they exist
    if (this.componentDefinition.class) {
      classes.push(this.componentDefinition.class);
    }
  
    // Add a width-related class if widthType is 'fixed' and buttonWidth is provided
    if (this.widthType === 'fixed' && this.buttonWidth) {
      // Generate a class like `btn-width-fixed-100px`
      const sanitizedWidth = this.buttonWidth.replace(/[^a-zA-Z0-9]/g, '');
      classes.push(`btn-width-fixed-${sanitizedWidth}`);
    }
  
    return classes;
  }
}
